import { clickedOnLoggedOutPage } from '@bridebook/web/app-basic/lib/analytics/tracking-actions/logged-out-page-click';
import { getSearchFilters, getTotalResults } from '@bridebook/web/app-basic/lib/search/selectors';
import { useSelector } from '@bridebook/web/app-basic/lib/utils';
import { memo, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { getPageSize } from '@bridebook/toolbox/src/search-suppliers/get-page-size';
import type { Slug } from '@bridebook/toolbox/src/types';
import Pagination from '@bridebook/web/app-basic/components/search/pagination/pagination';
import SearchPaginationComponent, {
  Location,
  SearchPaginationComponentChildrenProps,
} from '@bridebook/web/app-shared/components/search-pagination/search-pagination';
import { useMarket } from '@bridebook/web/app-shared/lib/i18n/hooks/use-market';
import { ISearchPageQuery } from '@bridebook/web/app-shared/lib/search/types';
import { getSearchPaginationUrl } from '@bridebook/web/lib/search/utils/get-search-pagination-url';

interface IProps {
  fullQuery: ISearchPageQuery;
  location: Location;
  facetsParams: string[];
  actualCategory: Slug;
}

// For SEO reasons it needs to have the same buttons in desktop and mobile
const MAX_BUTTONS = 3;

const SearchPagination = ({ fullQuery, location, facetsParams, actualCategory }: IProps) => {
  const totalResults = useSelector(getTotalResults);
  const filtersState = useSelector(getSearchFilters);
  const market = useMarket();
  const pageSize = getPageSize({ isLoggedIn: false, countryCode: market.country });

  const dispatch = useDispatch();

  const getUrlsAs = useCallback(
    (page: number) =>
      getSearchPaginationUrl({
        noSort: true,
        locationArea: location.query.area,
        locationSlug: location.query.slug,
        searchParams: fullQuery?.searchParams,
        page,
        filtersState,
        facetsParams,
        market,
      }).as,
    [
      facetsParams,
      filtersState,
      fullQuery?.searchParams,
      location.query.area,
      location.query.slug,
      market,
    ],
  );

  const onPageClick = useCallback(
    (_: Location, selected: number) => {
      dispatch(
        clickedOnLoggedOutPage({
          clickedLocation: 'search',
          clickedSection: 'pagination',
          cta: selected.toString(),
          supplierCountryCode: market.country,
          supplierCategory: actualCategory,
        }),
      );
      const urlSearchParams = getUrlsAs(selected).split('?')[1];
      const searchParams = urlSearchParams ? `?${urlSearchParams}` : '';
      window.location.assign(`${window.location.origin}${window.location.pathname}${searchParams}`);
    },
    [actualCategory, dispatch, getUrlsAs, market.country],
  );

  const hrefBuilder = (index: number): string => {
    const as = getUrlsAs(index);
    return `/${market.prefix}${as}`;
  };

  return (
    <SearchPaginationComponent
      fullQuery={fullQuery}
      location={location}
      totalResults={totalResults}
      pageSize={pageSize}
      onLoadMoreSuppliers={onPageClick}>
      {({ totalPages, activePage, onSelect }: SearchPaginationComponentChildrenProps) => (
        // The `maxButtons` is different in the last page to display more the
        // same items in that case, as the default behavior is to display one
        // fewer
        <Pagination
          items={totalPages}
          activePage={activePage}
          onSelect={onSelect}
          hrefBuilder={hrefBuilder}
          maxButtons={activePage === totalPages - 1 ? MAX_BUTTONS + 1 : MAX_BUTTONS}
        />
      )}
    </SearchPaginationComponent>
  );
};

export default memo(SearchPagination);
